<template>
    <div id="quiz-section" class="relative overflow-hidden">
        <div id="main-quiz-background" class="absolute w-full h-full"></div>
        <div v-if="showShareWarning" id="share-validate-warning-box" class="bg-orange">
            <div class="warning-content text-white">
                <i class="fas fa-exclamation-circle text-base"></i>
                <span class="text-xs">
                    {{ $t('share_validate_warning', { minimum: minimumQuestionToShare }) }}
                </span>
            </div>
        </div>
        <div
            @click="openThemeSettings()"
            :style="{
                color: themeTextColor,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderColor: themeTextColor,
                boxShadow: themeShadowColor
                    ? `inset 0 0 3px 0 ${hexToRgba(themeShadowColor, 80)}, 0 0 3px 0 ${hexToRgba(themeShadowColor, 80)}`
                    : 'none',
                cursor: !canEditQuiz ? 'not-allowed' : 'pointer'
            }"
            class="absolute tool-icon z-10"
        >
            <i
                :style="{
                    textShadow: themeShadowColor ? `2px 1px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                }"
                class="fas fa-pen"
            ></i>
        </div>

        <div
            v-if="quiz._id && canEditQuiz"
            @click="$emit('open-log')"
            :style="{
                color: themeTextColor,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderColor: themeTextColor,
                boxShadow: themeShadowColor
                    ? `inset 0 0 3px 0 ${hexToRgba(themeShadowColor, 80)}, 0 0 3px 0 ${hexToRgba(themeShadowColor, 80)}`
                    : 'none'
            }"
            class="absolute log-icon z-10 cursor-pointer"
        >
            <i
                :style="{
                    textShadow: themeShadowColor ? `2px 1px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                }"
                class="fas fa-file-alt"
            ></i>
        </div>

        <div
            :class="{
                'pb-4 relative': true,
                'pt-10': !approvalStatus && (quiz.share_setting.can_edit || owner),
                'pt-4': approvalStatus || (!quiz.share_setting.can_edit && !owner)
            }"
        >
            <div v-if="approvalStatus || (!quiz.share_setting.can_edit && !owner)" class="flex justify-center">
                <div
                    :class="[
                        {
                            'message-box': true,
                            'is-pending': approvalStatus == 'pending',
                            'is-rejected': approvalStatus == 'rejected'
                        }
                    ]"
                >
                    <span v-if="approvalStatus === 'pending'">{{ $t('setting_alert_checking') }}</span>
                    <span v-if="approvalStatus === 'rejected'" class="text-center">
                        {{ $t('setting_alert_not_approval') }}
                        <p>
                            <strong v-if="rejectMessage">"{{ rejectMessage }}"</strong>
                        </p>
                    </span>
                    <span v-if="!approvalStatus && !quiz.share_setting.can_edit && !owner">
                        {{ $t('setting_alert_cannot_edit') }}
                    </span>
                </div>
            </div>
            <QuizCreatorImage
                :image="this.getQuizImage(this.quiz, true)"
                :image-source="quiz.image_source"
                :showTool="false"
                :border-color="themeUiColor"
                :color="themeUiColor"
                id="main-quiz-image"
            />

            <h2
                :style="{
                    color: themeTextColor,
                    textShadow: themeShadowColor ? `2px 2px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                }"
                class="quiz-title text-white text-center"
            >
                {{ quiz.title ? quiz.title : '-' }}
            </h2>
            <p
                :style="{
                    color: themeTextColor,
                    textShadow: themeShadowColor ? `2px 2px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                }"
                class="text-white text-center"
            >
                {{ numberQuestionText }}
            </p>
            <p
                v-if="quiz.is_random_question_order"
                :style="{
                    color: themeTextColor,
                    textShadow: themeShadowColor ? `2px 2px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                }"
                class="text-center mt-1 text-sm"
            >
                <i
                    class="fas fa-random"
                    :style="{
                        textShadow: themeShadowColor ? `2px 2px 3px ${hexToRgba(themeShadowColor, 50)}` : 'none'
                    }"
                ></i>
                {{ $t('setting_header_random_question') }}
            </p>

            <div class="option-box flex justify-center mt-4">
                <XButton
                    @click="showQuizConfigSetting = true"
                    :bg-color="themeButtonColor"
                    :color="themeButtonTextColor"
                    class="shadow-text-none"
                    id="button-setting-quiz"
                >
                    <i class="fas fa-edit mr-1"></i>
                    <span>{{ $t('setting_button_setting_quiz') }}</span>
                </XButton>
                <a
                    v-if="quiz.type != 'poll' && quiz._id"
                    :href="`${quiz.url}?mode=preview`"
                    target="_blank"
                    class="no-underline"
                >
                    <XButton :bg-color="themeButtonColor" :color="themeButtonTextColor" class="shadow-text-none">
                        <i class="fas fa-eye"></i>
                        <span>{{ $t('setting_button_preview_quiz') }}</span>
                    </XButton>
                </a>
                <XButton
                    v-if="quiz._id && owner"
                    @click="openSharePopup()"
                    :bg-color="themeButtonColor"
                    :color="themeButtonTextColor"
                    class="shadow-text-none"
                >
                    <i class="fas fa-share-alt"></i>
                </XButton>
            </div>
        </div>

        <ThemeSettingModal v-if="showThemeSetting" @close="closeThemeSetting()" ref="themeSetting" />
        <QuizConfigSettingModal
            v-if="showQuizConfigSetting"
            @close="closeQuizConfigSetting()"
            @delete-quiz="$emit('delete-quiz', $event)"
            @open-share-validate-modal="$emit('open-share-validate-modal', $event)"
            ref="QuizConfigSetting"
        />
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { currentUser } from '@/lib/user.js'
import helperMixins from '@/mixins/helper.js'
import QuizCreatorImage from '@/components/quiz-creator/QuizCreatorImage'
import ThemeSettingModal from '@/components/quiz-creator/quiz-section/ThemeSettingModal'
import QuizConfigSettingModal from '@/components/quiz-creator/quiz-section/QuizConfigSettingModal'
import XButton from '@/components/v2/XButton'
import quizCreator from '@/mixins/quiz-creator.js'

export default {
    components: {
        QuizCreatorImage,
        ThemeSettingModal,
        QuizConfigSettingModal,
        XButton
    },

    mixins: [helperMixins, quizCreator],

    data() {
        return {
            showThemeSetting: false,
            showQuizConfigSetting: false
        }
    },

    mounted() {
        this.setBackground()
    },

    watch: {
        quiz: {
            handler() {
                this.setBackground()
            },
            deep: true
        }
    },

    computed: {
        ...mapState({
            quiz: state => state.question,
            approvalStatus: state => state.question.approval_status,
            rejectMessage: state => state.question.reject_message,
            showNumberQuestion: state => state.question.show_number_question,
            showSchoolList: state => state.question.show_school_list,
            showSchoolCountryList: state => state.question.show_school_country_list,
            minimumQuestionToShare: state => state.minimumQuestionToShare
        }),

        ...mapGetters({
            audioBg: 'getBgAudio',
            orderAbility: 'orderAbility'
        }),

        themeTextColor() {
            return this.quiz.themes[this.quiz.theme_name].question_text_color
        },

        themeShadowColor() {
            return this.quiz.themes[this.quiz.theme_name].question_shadow_color
        },

        themeButtonColor() {
            return this.quiz.themes[this.quiz.theme_name].button_color
        },

        themeButtonTextColor() {
            return this.quiz.themes[this.quiz.theme_name].button_text_color
        },

        themeUiColor() {
            return this.quiz.themes[this.quiz.theme_name].ui_color
        },

        showShareWarning() {
            if (
                ['teacher', 'teacher_plus'].includes(currentUser().role) &&
                (this.showSchoolList || this.showSchoolCountryList)
            ) {
                if (this.quiz.questions.filter(question => question.status).length < this.minimumQuestionToShare) {
                    return true
                } else {
                    return false
                }
            } else {
                return false
            }
        },

        availableQuestions() {
            return this.quiz.questions.filter(question => question.status === true).length
        },

        numberOfQuestion() {
            return this.quiz.show_number_question
        },

        numberQuestionText() {
            let showQuestion = 1
            let totalQuestion = 10

            if (this.availableQuestions <= this.numberOfQuestion) {
                showQuestion = this.availableQuestions
            } else {
                showQuestion = this.numberOfQuestion
            }

            if (this.numberOfQuestion > this.availableQuestions) {
                totalQuestion = this.numberOfQuestion
            } else {
                totalQuestion = this.availableQuestions
            }

            if (this.availableQuestions < 1) {
                return this.$t('landing_no_question')
            }

            if (showQuestion === totalQuestion) {
                return this.$t('landing_show_question_equal_question', {
                    question: totalQuestion,
                    s: totalQuestion > 1 ? 's' : ''
                })
            }

            return this.$t('landing_show_question_equal_question', {
                question: showQuestion,
                s: showQuestion > 1 ? 's' : ''
            })
        }
    },

    methods: {
        setBackground() {
            const quizSettingSection = document.querySelector('#main-quiz-background')

            const themeName = this.quiz.theme_name

            if (this.quiz.themes[themeName].background_color) {
                quizSettingSection.style.backgroundImage = 'unset'
                quizSettingSection.style.filter = 'unset'
                quizSettingSection.style.backgroundColor = this.quiz.themes[themeName].background_color
                document.documentElement.style.setProperty('--background-section-opacity', 0)
            }

            if (this.quiz.themes[themeName].background_image) {
                quizSettingSection.style.backgroundColor = this.quiz.themes[themeName].background_color
                quizSettingSection.style.filter = `blur(${this.quiz.background_image_blur}px)`

                //set to #main-quiz-background ::before
                document.documentElement.style.setProperty(
                    '--background-section-image',
                    `url(${this.quiz.themes[themeName].background_image})`
                )
                document.documentElement.style.setProperty(
                    '--background-section-opacity',
                    this.quiz.background_image_opacity + '%'
                )
            }
        },

        hexToRgba(hexCode, opacity) {
            var hex = hexCode.replace('#', '')

            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
            }

            var r = parseInt(hex.substring(0, 2), 16),
                g = parseInt(hex.substring(2, 4), 16),
                b = parseInt(hex.substring(4, 6), 16)

            return 'rgba(' + r + ',' + g + ',' + b + ',' + opacity / 100 + ')'
        },

        closeQuizConfigSetting() {
            this.setCacheData(this.quiz)
            this.showQuizConfigSetting = false
        },

        closeThemeSetting() {
            this.setCacheData(this.quiz)
            this.showThemeSetting = false
        },

        openThemeSettings() {
            if (!this.canEditQuiz) {
                return
            }

            this.showThemeSetting = true
        },

        openSharePopup() {
            if (!this.owner) return

            const baseUrl = process.env.VUE_APP_API_BASE_URL.replace('api/', '')
            const imagePath = this.quiz.image ? this.quiz.image : `${baseUrl}/images/${this.quiz.image_placeholder}`

            this.$emit('open-share-popup', {
                id: this.quiz._id,
                title: this.quiz.title,
                imagePath,
                sharedUser: this.quiz.shared_user
            })
        }
    }
}
</script>

<style lang="scss">
#quiz-section {
    min-height: 430px;

    #main-quiz-background {
        top: 0;
        left: 0;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;
            background-image: var(--background-section-image);
            opacity: var(--background-section-opacity);
        }
    }

    #share-validate-warning-box {
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        align-items: center;
        width: 100%;
        min-height: calc(24px + 1rem);
        padding: 0.5rem;

        box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.3);
        z-index: 10;

        .warning-content {
            display: flex;
            column-gap: 0.5rem;
            width: calc(100% - 24px - 0.5rem);
        }
    }

    .quiz-title {
        max-width: calc(100% - 1rem);
        margin-left: auto;
        margin-right: auto;
        display: -webkit-box;
        overflow: hidden;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    > .tool-icon {
        top: 0.5rem;
        right: 0.5rem;
        width: 24px;
        height: 24px;

        i {
            font-size: 12px;
        }
    }

    > .log-icon {
        top: 2.5rem;
        right: 0.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 24px;
        border: 1px solid #fff;
        border-radius: 50%;

        i {
            font-size: 12px;
        }
    }

    .option-box {
        column-gap: 0.5rem;
    }

    .quiz-creator-image-box #main-quiz-image.image-box .image {
        background-size: cover;
    }
}
</style>
